// Google Maps Service - Zentrale API Abstraktion für Places, Geocoding und Maps
// Verwendet die neue funktionale API von @googlemaps/js-api-loader

// Types
export interface PlacePrediction {
  placeId: string;
  description: string;
  mainText: string;
  secondaryText: string;
  types: string[];
}

export interface GeocodedLocation {
  latitude: number;
  longitude: number;
  formattedAddress: string;
  district?: string;
  city?: string;
  postalCode?: string;
  placeId?: string;
}

// Simple cache for results
interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

class GoogleMapsService {
  private loadPromise: Promise<void> | null = null;
  private isLoaded = false;
  private optionsSet = false;
  private autocompleteService: google.maps.places.AutocompleteService | null = null;
  private geocoder: google.maps.Geocoder | null = null;
  private sessionToken: google.maps.places.AutocompleteSessionToken | null = null;
  private predictionCache: Map<string, CacheEntry<PlacePrediction[]>> = new Map();
  private geocodeCache: Map<string, CacheEntry<GeocodedLocation>> = new Map();
  // Place class reference for the new API
  private PlaceClass: typeof google.maps.places.Place | null = null;

  constructor() {
    // Nothing to do in constructor anymore
  }

  // Set up API options (called once)
  private async setupOptions(): Promise<boolean> {
    if (this.optionsSet) return true;

    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    if (!apiKey || apiKey === 'YOUR_API_KEY_HERE') {
      console.warn('Google Maps API Key not configured. Location features will be limited.');
      return false;
    }

    try {
      // Dynamically import the loader
      const { setOptions } = await import('@googlemaps/js-api-loader');

      setOptions({
        key: apiKey,
        v: 'weekly',
        libraries: ['places', 'geocoding', 'maps', 'marker'],
        language: 'de',
        region: 'DE',
      });

      this.optionsSet = true;
      return true;
    } catch (error) {
      console.error('Failed to set Google Maps options:', error);
      return false;
    }
  }

  // Ensure the API is loaded before making calls
  async ensureLoaded(): Promise<boolean> {
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    if (!apiKey || apiKey === 'YOUR_API_KEY_HERE') {
      return false;
    }

    if (this.isLoaded) return true;
    if (this.loadPromise) {
      await this.loadPromise;
      return this.isLoaded;
    }

    this.loadPromise = (async () => {
      try {
        const optionsOk = await this.setupOptions();
        if (!optionsOk) {
          this.isLoaded = false;
          return;
        }

        // Dynamically import and load libraries
        const { importLibrary } = await import('@googlemaps/js-api-loader');

        await importLibrary('places');
        await importLibrary('geocoding');

        this.isLoaded = true;
        this.autocompleteService = new google.maps.places.AutocompleteService();
        this.geocoder = new google.maps.Geocoder();
        // Store Place class reference for new API (replaces deprecated PlacesService)
        this.PlaceClass = google.maps.places.Place;
        this.sessionToken = new google.maps.places.AutocompleteSessionToken();
      } catch (error: unknown) {
        console.error('Failed to load Google Maps API:', error);
        this.isLoaded = false;
      }
    })();

    await this.loadPromise;
    return this.isLoaded;
  }

  // Get new session token (call after selecting a prediction)
  refreshSessionToken(): void {
    if (this.isLoaded) {
      this.sessionToken = new google.maps.places.AutocompleteSessionToken();
    }
  }

  // Get autocomplete predictions for a search input
  async getPlacePredictions(
    input: string,
    options?: {
      types?: string[];
      bounds?: google.maps.LatLngBounds;
    }
  ): Promise<PlacePrediction[]> {
    if (input.length < 2) return [];

    // Check cache
    const cacheKey = `pred:${input}`;
    const cached = this.predictionCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.data;
    }

    const loaded = await this.ensureLoaded();
    if (!loaded || !this.autocompleteService) {
      return [];
    }

    try {
      const request: google.maps.places.AutocompletionRequest = {
        input,
        sessionToken: this.sessionToken || undefined,
        componentRestrictions: { country: 'de' },
        types: options?.types || ['(regions)'],
      };

      if (options?.bounds) {
        request.bounds = options.bounds;
      }

      const response = await this.autocompleteService.getPlacePredictions(request);

      const predictions: PlacePrediction[] = (response.predictions || []).map((p) => ({
        placeId: p.place_id,
        description: p.description,
        mainText: p.structured_formatting.main_text,
        secondaryText: p.structured_formatting.secondary_text || '',
        types: p.types || [],
      }));

      // Cache result
      this.predictionCache.set(cacheKey, {
        data: predictions,
        timestamp: Date.now(),
      });

      return predictions;
    } catch (error) {
      console.error('Autocomplete error:', error);
      return [];
    }
  }

  // Get place details (coordinates) from a place ID
  // Uses the new Place API (replaces deprecated PlacesService)
  async getPlaceDetails(placeId: string): Promise<GeocodedLocation | null> {
    // Check cache
    const cacheKey = `place:${placeId}`;
    const cached = this.geocodeCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.data;
    }

    const loaded = await this.ensureLoaded();
    if (!loaded || !this.PlaceClass) {
      return null;
    }

    try {
      // Create Place instance with the new API
      const place = new this.PlaceClass({ id: placeId });

      // Fetch the required fields
      await place.fetchFields({
        fields: ['location', 'formattedAddress', 'addressComponents'],
      });

      // Refresh session token after selection
      this.refreshSessionToken();

      // Extract location data
      const lat = place.location?.lat() || 0;
      const lng = place.location?.lng() || 0;
      const formattedAddress = place.formattedAddress || '';

      // Parse address components (convert from new format)
      const addressComponents = place.addressComponents || [];
      const location = this.parseAddressComponentsFromPlace(
        addressComponents,
        lat,
        lng,
        formattedAddress
      );
      location.placeId = placeId;

      // Cache result
      this.geocodeCache.set(cacheKey, {
        data: location,
        timestamp: Date.now(),
      });

      return location;
    } catch (error) {
      console.error('Place details error:', error);
      return null;
    }
  }

  // Parse address components from new Place API format
  private parseAddressComponentsFromPlace(
    components: google.maps.places.AddressComponent[],
    lat: number,
    lng: number,
    formattedAddress: string
  ): GeocodedLocation {
    let district: string | undefined;
    let city: string | undefined;
    let postalCode: string | undefined;

    for (const component of components) {
      const types = component.types || [];
      if (types.includes('sublocality') || types.includes('neighborhood')) {
        district = component.longText || component.shortText || undefined;
      }
      if (types.includes('locality')) {
        city = component.longText || component.shortText || undefined;
      }
      if (types.includes('postal_code')) {
        postalCode = component.longText || component.shortText || undefined;
      }
    }

    return {
      latitude: lat,
      longitude: lng,
      formattedAddress,
      district,
      city,
      postalCode,
    };
  }

  // Reverse geocode coordinates to address
  async reverseGeocode(lat: number, lng: number): Promise<GeocodedLocation | null> {
    // Check cache
    const cacheKey = `reverse:${lat.toFixed(4)},${lng.toFixed(4)}`;
    const cached = this.geocodeCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.data;
    }

    const loaded = await this.ensureLoaded();
    if (!loaded || !this.geocoder) {
      // Return basic location without address
      return {
        latitude: lat,
        longitude: lng,
        formattedAddress: 'Aktueller Standort',
      };
    }

    try {
      const response = await this.geocoder.geocode({
        location: { lat, lng },
      });

      if (response.results && response.results.length > 0) {
        // Find the most appropriate result (prefer neighborhood/sublocality)
        const result = response.results.find(
          (r) =>
            r.types.includes('sublocality') ||
            r.types.includes('neighborhood') ||
            r.types.includes('locality')
        ) || response.results[0];

        const location = this.parseAddressComponents(
          result.address_components,
          lat,
          lng,
          result.formatted_address
        );

        // Cache result
        this.geocodeCache.set(cacheKey, {
          data: location,
          timestamp: Date.now(),
        });

        return location;
      }

      return {
        latitude: lat,
        longitude: lng,
        formattedAddress: 'Aktueller Standort',
      };
    } catch (error) {
      console.error('Reverse geocode error:', error);
      return {
        latitude: lat,
        longitude: lng,
        formattedAddress: 'Aktueller Standort',
      };
    }
  }

  // Geocode an address string to coordinates
  async geocodeAddress(address: string): Promise<GeocodedLocation | null> {
    if (!address || address.length < 3) return null;

    // Check cache
    const cacheKey = `geocode:${address.toLowerCase()}`;
    const cached = this.geocodeCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.data;
    }

    const loaded = await this.ensureLoaded();
    if (!loaded || !this.geocoder) {
      return null;
    }

    try {
      const response = await this.geocoder.geocode({
        address,
        componentRestrictions: { country: 'de' },
      });

      if (response.results && response.results.length > 0) {
        const result = response.results[0];
        const location = this.parseAddressComponents(
          result.address_components,
          result.geometry.location.lat(),
          result.geometry.location.lng(),
          result.formatted_address
        );

        // Cache result
        this.geocodeCache.set(cacheKey, {
          data: location,
          timestamp: Date.now(),
        });

        return location;
      }

      return null;
    } catch (error) {
      console.error('Geocode error:', error);
      return null;
    }
  }

  // Parse Google address components into our format
  private parseAddressComponents(
    components: google.maps.GeocoderAddressComponent[],
    lat: number,
    lng: number,
    formattedAddress: string
  ): GeocodedLocation {
    let district: string | undefined;
    let city: string | undefined;
    let postalCode: string | undefined;

    for (const component of components) {
      if (component.types.includes('sublocality') || component.types.includes('sublocality_level_1')) {
        district = component.long_name;
      } else if (component.types.includes('neighborhood')) {
        if (!district) district = component.long_name;
      } else if (component.types.includes('locality')) {
        city = component.long_name;
      } else if (component.types.includes('postal_code')) {
        postalCode = component.long_name;
      }
    }

    return {
      latitude: lat,
      longitude: lng,
      formattedAddress,
      district,
      city,
      postalCode,
    };
  }

  // Clear all caches
  clearCache(): void {
    this.predictionCache.clear();
    this.geocodeCache.clear();
  }

  // Check if API is available
  isAvailable(): boolean {
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    return Boolean(apiKey && apiKey !== 'YOUR_API_KEY_HERE');
  }
}

// Singleton instance
export const googleMapsService = new GoogleMapsService();

export default googleMapsService;
